import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import NotFound from "@/pages/NotFound";
import DetailActions from "./components/DetailActions";
import { categoryMeta, getRelated, getVideoById } from "@/mocks/videos";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;

  useDocumentMeta({
    title: video ? `${video.title} - 69视频详情介绍` : "内容不存在-69视频",
    description: video ? video.summary.slice(0, 120) : "该内容可能已被移除或不存在。",
    keywords: video ? `${video.title},${video.genres.join(",")},69视频,${categoryMeta[video.type].label}` : "69视频",
    canonicalPath: video ? `/detail/${video.id}` : "/",
  });

  if (!video) return <NotFound />;

  const related = getRelated(video, 5);
  const meta = categoryMeta[video.type];
  const infoItems = [
    { label: "类型", value: meta.label },
    { label: "地区", value: video.region },
    { label: "年份", value: String(video.year) },
    { label: "时长", value: video.duration },
    { label: "导演", value: video.director },
    { label: "主演", value: video.cast.join(" / ") },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-950"></div>
        {video.banner && (
          <img
            src={video.banner}
            alt={`${video.title}高清封面`}
            title={`${video.title} - 69视频`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        )}
        <div
          className={`absolute inset-0 ${
            video.banner
              ? "bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/45"
              : "bg-gradient-to-t from-primary-950/80 via-primary-950/40 to-primary-700/20"
          }`}
        ></div>

        <div className="relative w-full px-4 md:px-8 pt-10 pb-10 md:pt-16 md:pb-14">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            <div className="hidden md:block w-40 shrink-0 rounded-lg overflow-hidden border border-background-100/20 aspect-[2/3]">
              <img
                src={video.cover}
                alt={`${video.title}海报`}
                title={`${video.title} - 69视频`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 mb-3">{video.title}</h1>
              <div className="flex items-center gap-3 text-sm text-background-100/90 mb-5 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-background-50/15 border border-background-100/20">{meta.label}</span>
                <span>{video.year}</span>
                <span>{video.region}</span>
                <span>{video.duration}</span>
                <span className="flex items-center gap-1 text-accent-400 font-semibold">
                  <i className="ri-star-fill" aria-hidden="true"></i>
                  {video.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                  {video.popularity} 热度
                </span>
              </div>
              <DetailActions videoId={video.id} title={video.title} />
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 pt-6 pb-10">
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: meta.label, path: `/category/${video.type}` },
              { label: video.title },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">剧情简介</h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">{video.summary}</p>

            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-primary-500" aria-hidden="true"></i>
                影片信息
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex gap-3 text-sm">
                    <dt className="text-foreground-500 shrink-0 w-14">{item.label}</dt>
                    <dd className="text-foreground-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6 p-4 rounded-lg bg-accent-50 border border-accent-200/60 flex items-start gap-3">
              <i className="ri-error-warning-line text-accent-600 text-lg mt-0.5" aria-hidden="true"></i>
              <p className="text-sm text-accent-900 leading-relaxed">
                本站为演示站点，仅展示视频封面与介绍信息，<strong>不提供真实播放与下载功能</strong>。如需观看，请通过正规授权渠道获取内容。
              </p>
            </div>
          </div>

          <aside>
            <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">所属分类</h2>
            <Link
              to={`/category/${video.type}`}
              className="flex items-center gap-3 rounded-lg border border-background-200/80 p-4 hover:border-primary-300 transition-colors cursor-pointer mb-8"
            >
              <span className="w-12 h-12 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                <i className={`${meta.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <span>
                <span className="block font-semibold text-foreground-950">{meta.label}</span>
                <span className="block text-xs text-foreground-500 mt-0.5">浏览更多{meta.label}内容</span>
              </span>
              <i className="ri-arrow-right-line ml-auto text-foreground-400" aria-hidden="true"></i>
            </Link>

            <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">相关标签</h2>
            <div className="flex flex-wrap gap-2">
              {[...video.genres, ...video.tags].map((tag) => (
                <Link
                  key={tag}
                  to={`/search?query=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-secondary-100 hover:text-secondary-800 transition-colors cursor-pointer"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6 flex items-center gap-2">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}